import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link } from "react-router-dom";
import { FaEye, FaClipboardList } from "react-icons/fa";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next";

export default function AdminOrders() {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [meta, setMeta] = useState(null);

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const fetchOrders = async (page = 1) => {
        setLoading(true);
        setErr("");

        try {
            const res = await api.get(`/admin/orders?page=${page}`);
            const data = res.data?.data ?? [];
            const metaData = res.data?.meta ?? null;

            setOrders(data);
            setMeta(metaData);
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_orders_load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(1);
    }, []);

    const getStatusInfo = (status) => {
        switch (status?.toLowerCase()) {
            case "paid": return { color: "success", label: t("status_paid") };
            case "pending": return { color: "warning", label: t("status_pending") };
            case "shipped": return { color: "primary", label: t("status_shipped") };
            case "cancelled": return { color: "danger", label: t("status_cancelled") };
            default: return { color: "secondary", label: status || t("status_unknown") };
        }
    };

    return (
        <div className="container-fluid p-0 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <div className="d-flex align-items-center">
                    <FaClipboardList className="fs-3 text-primary me-3" />
                    <h2 className="m-0 fw-bold text-dark">{t("admin_orders_title")}</h2>
                </div>
            </div>

            {err && <AlertBox type="danger" message={err} onClose={() => setErr("")} />}

            <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header bg-white pt-4 pb-0 border-bottom-0 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0">{t("admin_orders_history")}</h5>
                </div>
                
                <div className="card-body p-0 mt-3">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                            <p className="mt-3 text-muted fw-bold">{t("admin_orders_loading")}</p>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="alert alert-info m-4 text-center py-5 rounded-3">
                            <FaClipboardList className="display-4 mb-3 opacity-50" />
                            <h5 className="m-0">{t("admin_orders_no_found")}</h5>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0 text-center">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="py-3">{t("admin_orders_col_id")}</th>
                                            <th className="py-3">{t("admin_orders_col_customer")}</th>
                                            <th className="py-3">{t("admin_orders_col_status")}</th>
                                            <th className="py-3">{t("admin_orders_col_total")}</th>
                                            <th className="py-3">{t("admin_orders_col_date")}</th>
                                            <th className="py-3">{t("admin_orders_col_actions")}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {orders.map((o) => {
                                            const statusInfo = getStatusInfo(o.status);
                                            return (
                                                <tr key={o.id}>
                                                    <td className="fw-bold text-muted">#{o.id}</td>
                                                    <td className="fw-bold text-dark">{o.user?.name || o.user_id}</td>
                                                    <td>
                                                        <span className={`badge bg-${statusInfo.color} px-3 py-2 rounded-pill shadow-sm`}>
                                                            {statusInfo.label}
                                                        </span>
                                                    </td>
                                                    <td className="text-success fw-bold">${o.total}</td>
                                                    <td className="text-muted small fw-bold">
                                                        {o.created_at ? o.created_at.slice(0, 10) : "-"}
                                                    </td>
                                                    <td>
                                                        <Link to={`/admin/orders/${o.id}`} className="btn btn-outline-primary btn-sm px-3 rounded-pill">
                                                            <FaEye className="me-1" /> {t("admin_orders_btn_view")}
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {meta && meta.last_page > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-outline-secondary btn-sm px-3" disabled={meta.current_page <= 1} onClick={() => fetchOrders(meta.current_page - 1)}>
                                            {t("admin_orders_prev")}
                                        </button>
                                        <button className="btn btn-outline-secondary btn-sm px-3" disabled={meta.current_page >= meta.last_page} onClick={() => fetchOrders(meta.current_page + 1)}>
                                            {t("admin_orders_next")}
                                        </button>
                                    </div>
                                    <span className="text-muted small">
                                        {t("page_x_of_y", { current: meta.current_page, last: meta.last_page })}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}